import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';

interface IHandle {
  remove(): void;
}

export type GraphicHitTest = {
  onHover(callback: (graphic: Graphic | undefined) => void): void;
  onClick(callback: (graphic: Graphic | undefined) => void): void;
  getLastHitGraphic(): Graphic | undefined;
  removeHandlers(): void;
}

const GraphicHitTester = (view: MapView, graphicsLayer: GraphicsLayer): GraphicHitTest => {
  const mapView = view;
  const localGraphicsLayer = graphicsLayer;
  let lastHitGraphic: Graphic | undefined;
  let hoverHandler: IHandle | undefined;
  let clickHandler: IHandle | undefined;
  let isProcessingHitTest: boolean = false;

  const processHitTest = async (
    event: any,
    callback: (graphic: Graphic | undefined) => void,
  ) => {
    if (isProcessingHitTest) {
      console.warn('Hit test already in progress, ignoring event');
      return;
    }
    isProcessingHitTest = true;

    try {
      const hitResults = (await mapView.hitTest(event)).results;
      const targetLayerResponse = hitResults.filter((response) => response?.type === 'graphic' && response?.graphic?.layer?.id === localGraphicsLayer.id);
      const hitResponse = targetLayerResponse[0] || {};
      const foundGraphic = hitResponse.type === 'graphic' && hitResponse ? hitResponse.graphic : undefined;
      const isSameGraphicHit = foundGraphic?.attributes?.shape.item?._id
        === lastHitGraphic?.attributes?.shape.item?._id;
      const isClickEvent = event.type === 'click';
      const validHoveredGraphic = foundGraphic && !isSameGraphicHit;
      const validClickedGraphic = foundGraphic && isClickEvent;

      if (validHoveredGraphic || validClickedGraphic) {
        lastHitGraphic = foundGraphic;
        callback(foundGraphic);
      } else if (!foundGraphic && lastHitGraphic) {
        lastHitGraphic = undefined;
        callback(undefined);
      }
    } catch (error) {
      console.error('Error during hitTest:', error);
    } finally {
      isProcessingHitTest = false;
    }
  };

  const onHover = (callback: (graphic: Graphic | undefined) => void): void => {
    if (hoverHandler) {
      hoverHandler.remove();
    }
    hoverHandler = mapView.on('pointer-move', (event) => processHitTest(event, callback));
  };

  const onClick = (callback: (graphic: Graphic | undefined) => void): void => {
    if (clickHandler) {
      clickHandler.remove();
    }
    clickHandler = mapView.on('click', (event) => processHitTest(event, callback));
  };

  const getLastHitGraphic = (): Graphic | undefined => lastHitGraphic;

  const removeHandlers = (): void => {
    if (hoverHandler) {
      hoverHandler.remove();
      hoverHandler = undefined;
    }
    if (clickHandler) {
      clickHandler.remove();
      clickHandler = undefined;
    }
  };

  return {
    onHover,
    onClick,
    getLastHitGraphic,
    removeHandlers,
  };
};

export default GraphicHitTester;
